import React, { useEffect, useMemo, useState } from 'react';
import { CSVLink } from 'react-csv';
import {
    MdCheckCircle,
    MdFavorite,
    MdLayers,
    MdMap,
    MdPlace,
    MdPlayArrow,
    MdSchedule,
    MdSettings,
    MdStop,
    MdTimer,
    MdTrendingUp,
    MdTune,
    MdOpacity,
    MdScience,
} from 'react-icons/md';
import { injectIntl, intlShape } from 'react-intl';
import { toast } from 'react-toastify';

import PropTypes from 'prop-types';

import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    InputLabel,
    LinearProgress,
    MenuItem,
    Paper,
    Select,
    Tab,
    Tabs,
    Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import LocaleMessage from '~/components/LocaleMessage';

import GetFileName from '~/util/GetFileName';

import { formatFluidVolume } from './cleaningReportConsumptionUtils';
import { GENERIC_DETAIL_ERROR } from './cleaningReportDetailConstants';
import CleaningReportMapGallery, {
    CLEANING_REPORT_MAP_SLOT_KEYS,
} from './CleaningReportMapGallery';
import {
    formatCleaningConfigValueDisplay,
    getCleaningAreaTypeLabel,
    getCleaningConfigLevelLabel012,
    getCleaningConfigParamLabel,
    getCleaningSubModeLabel,
} from './cleaningReportPuduCategories';
import {
    formatCleaningDuration,
    formatCleaningReportCreateDateTime,
    formatCleaningReportUnixSeconds,
    getCleaningModeLabel,
    getCleaningTaskStatusLabel,
    parseCleaningReportJsonField,
    getCleaningReportCsvHeaders,
    buildCleaningReportCsvData,
} from './cleaningReportUtils';

const CONFIG_KEY_ORDER = [
    'left_brush',
    'right_brush',
    'type',
    'vacuum_suction',
    'wash_suction',
    'mode',
    'right_vacuum_suction',
    'vacuum_speed',
    'wash_speed',
    'wash_wash',
];

function localeToBcp47(code) {
    if (!code) return 'pt-BR';
    const map = { pt_BR: 'pt-BR', en_US: 'en-US', en_GB: 'en-GB' };
    return map[code] || 'pt-BR';
}

function dash(v) {
    if (v === null || v === undefined || v === '') return '—';
    return v;
}

function getCreateTimeMs(createTime) {
    if (createTime == null || createTime === '') return NaN;
    if (typeof createTime === 'number' && Number.isFinite(createTime)) {
        return createTime > 1e12 ? createTime : createTime * 1000;
    }
    const s = String(createTime).trim();
    if (!s) return NaN;
    const withT = /^\d{4}-\d{2}-\d{2}\s/.test(s) ? s.replace(/\s+/, 'T') : s;
    const ms = Date.parse(withT);
    if (!Number.isNaN(ms)) return ms;
    return NaN;
}

function formatDetailHeaderSubtitle(createTime, bcp) {
    const ms = getCreateTimeMs(createTime);
    if (!Number.isFinite(ms)) {
        return formatCleaningReportCreateDateTime(createTime);
    }
    const d = new Date(ms);
    const dateStr = d.toLocaleDateString(bcp, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    });
    const timeStr = d.toLocaleTimeString(bcp, {
        hour: '2-digit',
        minute: '2-digit',
    });
    return `${dateStr} · ${timeStr}`;
}

function sortedConfigKeys(obj) {
    const keys = Object.keys(obj);
    const ordered = CONFIG_KEY_ORDER.filter(k => keys.includes(k));
    const rest = keys.filter(k => !ordered.includes(k)).sort();
    return [...ordered, ...rest];
}

function filledSegmentsForLevel(raw) {
    const n = Number(raw);
    if (!Number.isFinite(n)) return 0;
    if (n <= 0) return 0;
    if (n >= 2) return 3;
    return 2;
}

function looksLikeLevel012Value(key, rawValue) {
    const k = String(key);
    const n = Number(rawValue);
    return (
        /speed|suction|wash|brush|^mode$|^type$/i.test(k) &&
        Number.isFinite(n) &&
        n >= 0 &&
        n <= 2
    );
}

const useStyles = makeStyles(theme => ({
    dialogPaper: {
        maxWidth: 782,
        width: '100%',
        margin: theme.spacing(2),
        [theme.breakpoints.down('xs')]: {
            maxWidth: 'calc(100% - 16px)',
            margin: theme.spacing(1),
        },
    },
    dialogContent: {
        padding: theme.spacing(1.5, 2),
        '&:first-child': {
            paddingTop: theme.spacing(1.5),
        },
    },
    dialogActions: {
        padding: theme.spacing(1, 2),
    },
    dialogTitle: {
        padding: theme.spacing(2.5, 3),
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    titleLoading: {
        fontSize: '1rem',
        fontWeight: 600,
    },
    titleMain: {
        fontSize: '1.125rem',
        fontWeight: 600,
        lineHeight: 1.3,
    },
    titleSubtitle: {
        fontSize: '0.75rem',
        marginTop: 4,
        lineHeight: 1.35,
    },
    titleRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: theme.spacing(1.5),
    },
    sectionCaps: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.75),
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(1),
        fontSize: '0.625rem',
        fontWeight: 700,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        color: theme.palette.text.secondary,
    },
    sectionIcon: {
        display: 'flex',
        color: theme.palette.primary.main,
        opacity: 0.9,
    },
    metricCard: {
        display: 'flex',
        alignItems: 'stretch',
        borderRadius: 10,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        overflow: 'hidden',
        height: '100%',
        minHeight: 68,
    },
    metricIconBox: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 44,
        flexShrink: 0,
        backgroundColor:
            theme.palette.type === 'dark'
                ? 'rgba(255,255,255,0.06)'
                : theme.palette.grey[100],
        color: theme.palette.text.secondary,
        fontSize: '1.1rem',
    },
    metricBody: {
        flex: 1,
        padding: theme.spacing(0.75, 1.25),
        minWidth: 0,
    },
    metricLabel: {
        fontSize: '0.625rem',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(0.25),
    },
    metricValue: {
        fontSize: '0.875rem',
        fontWeight: 700,
        lineHeight: 1.3,
        wordBreak: 'break-word',
    },
    progressRow: {
        marginTop: theme.spacing(1.25),
    },
    progressHead: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: theme.spacing(0.5),
    },
    progressCaptionWithIcon: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.75),
        minWidth: 0,
    },
    progressLabelIcon: {
        display: 'flex',
        flexShrink: 0,
        color: theme.palette.text.secondary,
        fontSize: '1.125rem',
    },
    progressCaption: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
    },
    progressPercent: {
        fontSize: '0.75rem',
        fontWeight: 700,
    },
    sectionCapsConfig: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.75),
        marginTop: theme.spacing(2.25),
        marginBottom: theme.spacing(1.25),
        fontSize: '0.6875rem',
        fontWeight: 700,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        color: theme.palette.text.secondary,
    },
    intensityItem: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 12,
        padding: theme.spacing(1, 1.25),
        marginBottom: theme.spacing(1),
        '&:last-child': {
            marginBottom: 0,
        },
    },
    intensityRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        flexWrap: 'wrap',
    },
    intensityLabel: {
        flex: '0 0 60%',
        maxWidth: '44%',
        fontSize: '0.8rem',
        fontWeight: 400,
        color: theme.palette.text.primary,
        lineHeight: 1.35,
        [theme.breakpoints.down('xs')]: {
            flex: '1 1 100%',
            maxWidth: '100%',
            marginBottom: theme.spacing(0.5),
        },
    },
    intensityBar: {
        display: 'flex',
        gap: 6,
        flexShrink: 0,
    },
    intensitySeg: {
        width: 22,
        height: 9,
        borderRadius: 4,
        backgroundColor: theme.palette.grey[300],
        [theme.breakpoints.down('xs')]: {
            width: 20,
        },
    },
    intensitySegOn: {
        backgroundColor: theme.palette.primary.main,
    },
    intensityCaption: {
        fontSize: '0.8rem',
        fontWeight: 500,
        color: theme.palette.text.secondary,
        flex: 1,
        textAlign: 'right',
        minWidth: 0,
        lineHeight: 1.35,
        [theme.breakpoints.down('xs')]: {
            flex: '1 1 100%',
            textAlign: 'left',
            marginTop: theme.spacing(0.5),
        },
    },
    zonesEmptyHint: {
        fontSize: '0.8125rem',
        color: theme.palette.text.secondary,
    },
    sectionCapsZones: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.75),
        marginTop: theme.spacing(2.25),
        marginBottom: theme.spacing(1.25),
        fontSize: '0.6875rem',
        fontWeight: 700,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        color: theme.palette.text.secondary,
    },
    zonesTableWrap: {
        borderRadius: 10,
        border: `1px solid ${theme.palette.divider}`,
        overflow: 'hidden',
        fontSize: '0.875rem',
        lineHeight: 1.45,
        '& table': {
            marginBottom: 0,
        },
        '& thead th': {
            fontSize: '0.7rem',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            fontWeight: 700,
            color: theme.palette.text.secondary,
            borderBottom: `1px solid ${theme.palette.divider}`,
            paddingTop: theme.spacing(1),
            paddingBottom: theme.spacing(1),
        },
        '& tbody td': {
            verticalAlign: 'middle',
            paddingTop: theme.spacing(1),
            paddingBottom: theme.spacing(1),
            paddingLeft: theme.spacing(1.25),
            paddingRight: theme.spacing(1.25),
        },
    },
    typeChip: {
        height: 26,
        fontSize: '0.75rem',
        fontWeight: 600,
    },
    mapTabsRoot: {
        marginTop: theme.spacing(0.25),
        borderBottom: `1px solid ${theme.palette.divider}`,
        minHeight: 36,
    },
    mapTab: {
        minHeight: 36,
        textTransform: 'none',
        fontWeight: 600,
        fontSize: '0.75rem',
        paddingTop: theme.spacing(0.5),
        paddingBottom: theme.spacing(0.5),
    },
    mapPanel: {
        paddingTop: theme.spacing(1.25),
    },
    bodyMuted: {
        fontSize: '0.75rem',
    },
    loadingHelper: {
        fontSize: '0.75rem',
        marginTop: theme.spacing(1),
        color: theme.palette.text.secondary,
    },
    errorText: {
        fontSize: '0.8125rem',
    },
    compactPaper: {
        padding: theme.spacing(1, 1.5),
        borderRadius: 10,
        border: '1px solid rgba(0,0,0,0.08)',
    },
    galleryNoSection: {
        '& > div:first-of-type': {
            marginTop: 0,
        },
    },
}));

function SectionCapsTitle({ classes, icon, msg, capsClassName }) {
    return (
        <Typography
            component="div"
            className={capsClassName || classes.sectionCaps}
        >
            <span className={classes.sectionIcon}>{icon}</span>
            <LocaleMessage msg={msg} />
        </Typography>
    );
}

SectionCapsTitle.propTypes = {
    classes: PropTypes.objectOf(PropTypes.string).isRequired,
    icon: PropTypes.node.isRequired,
    msg: PropTypes.string.isRequired,
    capsClassName: PropTypes.string,
};

SectionCapsTitle.defaultProps = {
    capsClassName: null,
};

function MetricCard({ classes, icon, labelMsg, value }) {
    return (
        <div className={classes.metricCard}>
            <div className={classes.metricIconBox}>{icon}</div>
            <div className={classes.metricBody}>
                <Typography className={classes.metricLabel} component="div">
                    <LocaleMessage msg={labelMsg} />
                </Typography>
                <Typography className={classes.metricValue} component="div">
                    {value}
                </Typography>
            </div>
        </div>
    );
}

MetricCard.propTypes = {
    classes: PropTypes.objectOf(PropTypes.string).isRequired,
    icon: PropTypes.node.isRequired,
    labelMsg: PropTypes.string.isRequired,
    value: PropTypes.node.isRequired,
};

function LabeledLinearProgress({
    classes,
    labelMsg,
    valuePct,
    barColor,
    icon,
}) {
    const v = Math.min(100, Math.max(0, Number(valuePct) || 0));
    return (
        <div className={classes.progressRow}>
            <div className={classes.progressHead}>
                <div className={classes.progressCaptionWithIcon}>
                    {icon ? (
                        <span className={classes.progressLabelIcon}>
                            {icon}
                        </span>
                    ) : null}
                    <Typography
                        component="div"
                        className={classes.progressCaption}
                    >
                        <LocaleMessage msg={labelMsg} />
                    </Typography>
                </div>
                <Typography component="div" className={classes.progressPercent}>
                    {Math.round(v)}%
                </Typography>
            </div>
            <LinearProgress
                variant="determinate"
                value={v}
                style={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: 'rgba(0,0,0,0.08)',
                }}
                color={barColor || 'primary'}
            />
        </div>
    );
}

LabeledLinearProgress.propTypes = {
    classes: PropTypes.objectOf(PropTypes.string).isRequired,
    labelMsg: PropTypes.string.isRequired,
    valuePct: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    barColor: PropTypes.string,
    icon: PropTypes.node,
};

LabeledLinearProgress.defaultProps = {
    valuePct: 0,
    barColor: 'primary',
    icon: null,
};

function IntensityParam({ classes, label, rawValue, localeCode }) {
    const filled = looksLikeLevel012Value(label, rawValue)
        ? filledSegmentsForLevel(rawValue)
        : 0;
    const caption = looksLikeLevel012Value(label, rawValue)
        ? getCleaningConfigLevelLabel012(rawValue, localeCode)
        : formatCleaningConfigValueDisplay(label, rawValue, localeCode);

    return (
        <div className={classes.intensityItem}>
            <div className={classes.intensityRow}>
                <Typography className={classes.intensityLabel} component="div">
                    {getCleaningConfigParamLabel(label, localeCode)}
                </Typography>
                <div className={classes.intensityBar}>
                    {[0, 1, 2].map(i => (
                        <span
                            key={i}
                            className={`${classes.intensitySeg} ${
                                i < filled ? classes.intensitySegOn : ''
                            }`}
                        />
                    ))}
                </div>
                <Typography
                    className={classes.intensityCaption}
                    component="div"
                >
                    {caption}
                </Typography>
            </div>
        </div>
    );
}

IntensityParam.propTypes = {
    classes: PropTypes.objectOf(PropTypes.string).isRequired,
    label: PropTypes.string.isRequired,
    rawValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    localeCode: PropTypes.string.isRequired,
};

IntensityParam.defaultProps = {
    rawValue: undefined,
};

function statusChipColor(status) {
    const s = Number(status);
    if (s === 1) return 'primary';
    if (s === 4) return 'default';
    if (s === 5 || s === 6) return 'secondary';
    return 'default';
}

function CleaningReportDetailModal({
    intl,
    open,
    onClose,
    loading,
    errorMessage,
    detail,
    localeCode,
    chemicalDilutionRatio,
}) {
    const classes = useStyles();
    const bcp = localeToBcp47(localeCode);

    const configObj = detail
        ? parseCleaningReportJsonField(detail.config)
        : null;
    const floorsRaw = detail
        ? parseCleaningReportJsonField(detail.floor_list)
        : null;
    const floors = useMemo(() => (Array.isArray(floorsRaw) ? floorsRaw : []), [
        floorsRaw,
    ]);

    const [floorIndex, setFloorIndex] = useState(0);
    const [mapTab, setMapTab] = useState(0);

    const [zoneTab, setZoneTab] = useState(0);
    const [expandedZones, setExpandedZones] = useState(false);
    const [expandedRoutes, setExpandedRoutes] = useState(false);

    useEffect(() => {
        if (open && detail) {
            setFloorIndex(0);
            setMapTab(0);
            setZoneTab(0);
            setExpandedZones(false);
            setExpandedRoutes(false);
        }
    }, [open, detail]);

    const selectedFloor = floors[floorIndex] || null;

    const floorPickerLabel = intl.formatMessage({
        id: 'page.robots.cleaning_report.detail.floor_picker',
    });

    const { cleaningZoneRows, routeZoneRows } = useMemo(() => {
        const cRows = [];
        const rRows = [];
        floors.forEach(f => {
            const arr = Array.isArray(f.area_array) ? f.area_array : [];
            arr.forEach(a => {
                const row = {
                    ...a,
                    _mapName: f.map_name,
                };
                if (a.type === 0 || a.type === 1) {
                    cRows.push(row);
                } else {
                    rRows.push(row);
                }
            });
        });
        return { cleaningZoneRows: cRows, routeZoneRows: rRows };
    }, [floors]);

    const titleText = detail && detail.task_name ? detail.task_name : null;

    const headerSubtitle =
        detail && !loading && !errorMessage
            ? formatDetailHeaderSubtitle(
                  detail.end_time || detail.start_time,
                  bcp
              )
            : '';

    const completionPct =
        detail && detail.percentage != null ? Number(detail.percentage) : 0;

    let batteryPct = null;
    if (detail && detail.battery != null) {
        batteryPct = Number(detail.battery);
    } else if (detail && detail.cost_battery != null) {
        batteryPct = Math.min(100, Math.max(0, Number(detail.cost_battery)));
    }

    const configEntries =
        configObj && typeof configObj === 'object' && !Array.isArray(configObj)
            ? sortedConfigKeys(configObj).map(k => ({
                  key: k,
                  value: configObj[k],
              }))
            : [];

    const midConfig = Math.ceil(configEntries.length / 2);
    const configLeft = configEntries.slice(0, midConfig);
    const configRight = configEntries.slice(midConfig);

    const { csvHeaders, csvData } = useMemo(() => {
        if (!detail) return { csvHeaders: [], csvData: [] };

        const hHeaders = getCleaningReportCsvHeaders(
            intl,
            !!(detail && detail._robot),
            true
        );
        const hDataList = buildCleaningReportCsvData(
            [detail],
            localeCode,
            !!(detail && detail._robot),
            true
        );

        if (!hDataList || hDataList.length === 0) {
            return { csvHeaders: [], csvData: [] };
        }

        const row = hDataList[0];

        const transposedHeaders = [
            {
                label: intl.formatMessage({
                    id: 'page.robots.cleaning_report.csv.col_property',
                    defaultMessage: 'Propriedade',
                }),
                key: 'property',
            },
            {
                label: intl.formatMessage({
                    id: 'page.robots.cleaning_report.csv.col_value',
                    defaultMessage: 'Valor',
                }),
                key: 'value',
            },
        ];

        const transposedData = hHeaders.map(h => ({
            property: h.label,
            value: row[h.key] != null ? row[h.key] : '',
        }));

        return { csvHeaders: transposedHeaders, csvData: transposedData };
    }, [intl, detail, localeCode]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={false}
            fullWidth
            scroll="paper"
            classes={{ paper: classes.dialogPaper }}
        >
            <DialogTitle disableTypography className={classes.dialogTitle}>
                {loading || errorMessage || !detail ? (
                    <Typography
                        component="div"
                        className={classes.titleLoading}
                    >
                        <LocaleMessage msg="page.robots.cleaning_report.detail.title" />
                    </Typography>
                ) : (
                    <div className={classes.titleRow}>
                        <Box flex={1} minWidth={0}>
                            <Typography
                                component="div"
                                className={classes.titleMain}
                            >
                                {titleText || (
                                    <LocaleMessage msg="page.robots.cleaning_report.detail.title" />
                                )}
                            </Typography>
                            <Typography
                                component="div"
                                color="textSecondary"
                                className={classes.titleSubtitle}
                            >
                                {headerSubtitle}
                            </Typography>
                        </Box>
                        <Chip
                            size="small"
                            color={statusChipColor(detail.status)}
                            label={getCleaningTaskStatusLabel(
                                detail.status,
                                localeCode
                            )}
                            style={{ flexShrink: 0 }}
                        />
                    </div>
                )}
            </DialogTitle>
            <DialogContent dividers className={classes.dialogContent}>
                {loading ? (
                    <div className="text-center py-4">
                        <CircularProgress size={28} />
                        <Typography
                            component="div"
                            className={classes.loadingHelper}
                        >
                            <LocaleMessage msg="page.robots.cleaning_report.detail.loading" />
                        </Typography>
                    </div>
                ) : null}

                {!loading && errorMessage ? (
                    <Typography
                        color="error"
                        className={classes.errorText}
                        component="div"
                    >
                        {errorMessage === GENERIC_DETAIL_ERROR ? (
                            <LocaleMessage msg="page.robots.cleaning_report.detail.error" />
                        ) : (
                            errorMessage
                        )}
                    </Typography>
                ) : null}

                {!loading && !errorMessage && detail ? (
                    <>
                        <SectionCapsTitle
                            classes={classes}
                            icon={<MdSchedule size={16} />}
                            msg="page.robots.cleaning_report.detail.section.time_caps"
                        />
                        <Grid container spacing={1}>
                            <Grid item xs={12} sm={4}>
                                <MetricCard
                                    classes={classes}
                                    icon={<MdPlayArrow size={18} />}
                                    labelMsg="page.robots.cleaning_report.detail.metric.start"
                                    value={formatCleaningReportUnixSeconds(
                                        detail.start_time,
                                        bcp
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <MetricCard
                                    classes={classes}
                                    icon={<MdStop size={18} />}
                                    labelMsg="page.robots.cleaning_report.detail.metric.end"
                                    value={formatCleaningReportUnixSeconds(
                                        detail.end_time,
                                        bcp
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <MetricCard
                                    classes={classes}
                                    icon={<MdTimer size={18} />}
                                    labelMsg="page.robots.cleaning_report.detail.metric.duration"
                                    value={formatCleaningDuration(
                                        detail.clean_time
                                    )}
                                />
                            </Grid>
                        </Grid>

                        <SectionCapsTitle
                            classes={classes}
                            icon={<MdTrendingUp size={16} />}
                            msg="page.robots.cleaning_report.detail.section.areas_progress_caps"
                        />
                        <Grid container spacing={1}>
                            <Grid item xs={12} sm={4}>
                                <MetricCard
                                    classes={classes}
                                    icon={<MdPlace size={18} />}
                                    labelMsg="page.robots.cleaning_report.detail.metric.cleaned_area_short"
                                    value={
                                        detail.clean_area != null
                                            ? `${Number(
                                                  detail.clean_area
                                              ).toFixed(2)} m²`
                                            : '—'
                                    }
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <MetricCard
                                    classes={classes}
                                    icon={<MdLayers size={18} />}
                                    labelMsg="page.robots.cleaning_report.detail.metric.task_area_short"
                                    value={
                                        detail.task_area != null
                                            ? `${Number(
                                                  detail.task_area
                                              ).toFixed(2)} m²`
                                            : '—'
                                    }
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <MetricCard
                                    classes={classes}
                                    icon={<MdTrendingUp size={18} />}
                                    labelMsg="page.robots.cleaning_report.detail.metric.avg_area_short"
                                    value={
                                        detail.average_area != null
                                            ? `${Number(
                                                  detail.average_area
                                              ).toFixed(2)} m²`
                                            : '—'
                                    }
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Paper
                                    elevation={0}
                                    className={classes.compactPaper}
                                >
                                    <LabeledLinearProgress
                                        classes={classes}
                                        icon={<MdCheckCircle size={18} />}
                                        labelMsg="page.robots.cleaning_report.detail.completion"
                                        valuePct={completionPct}
                                    />
                                </Paper>
                            </Grid>
                        </Grid>

                        <SectionCapsTitle
                            classes={classes}
                            icon={<MdTimer size={16} />}
                            msg="page.robots.cleaning_report.detail.section.consumption_caps"
                        />
                        <Paper elevation={0} className={classes.compactPaper}>
                            {batteryPct != null && !Number.isNaN(batteryPct) ? (
                                <LabeledLinearProgress
                                    classes={classes}
                                    labelMsg="page.robots.cleaning_report.detail.battery_consumption"
                                    valuePct={batteryPct}
                                    barColor="secondary"
                                />
                            ) : (
                                <Typography
                                    color="textSecondary"
                                    component="div"
                                    className={classes.bodyMuted}
                                >
                                    <LocaleMessage msg="page.robots.cleaning_report.detail.battery_consumption" />
                                    {': '}—
                                </Typography>
                            )}
                        </Paper>
                        {detail && (
                            <Grid
                                container
                                spacing={1}
                                style={{ marginTop: 8 }}
                            >
                                <Grid item xs={12} sm={6}>
                                    <MetricCard
                                        classes={classes}
                                        icon={<MdOpacity size={18} />}
                                        labelMsg="page.robots.cleaning_report.detail.field.cost_water"
                                        value={
                                            detail.cost_water != null
                                                ? `${(
                                                      Number(
                                                          detail.cost_water
                                                      ) / 1000
                                                  ).toFixed(2)} L`
                                                : '—'
                                        }
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <MetricCard
                                        classes={classes}
                                        icon={<MdScience size={18} />}
                                        labelMsg="page.robots.cleaning_report.consumption.product_spent"
                                        value={
                                            detail.cost_water != null
                                                ? `${
                                                      formatFluidVolume(
                                                          (Number(
                                                              detail.cost_water
                                                          ) /
                                                              1000) *
                                                              (Number(
                                                                  chemicalDilutionRatio
                                                              ) || 10)
                                                      ).value
                                                  } ${
                                                      formatFluidVolume(
                                                          (Number(
                                                              detail.cost_water
                                                          ) /
                                                              1000) *
                                                              (Number(
                                                                  chemicalDilutionRatio
                                                              ) || 10)
                                                      ).unit
                                                  }`
                                                : '—'
                                        }
                                    />
                                </Grid>
                            </Grid>
                        )}

                        <SectionCapsTitle
                            classes={classes}
                            icon={<MdSettings size={16} />}
                            msg="page.robots.cleaning_report.detail.section.robot_caps"
                        />
                        <Grid container spacing={1}>
                            <Grid item xs={12} sm={4}>
                                <MetricCard
                                    classes={classes}
                                    icon={<MdSettings size={18} />}
                                    labelMsg="page.robots.cleaning_report.col_mode"
                                    value={getCleaningModeLabel(
                                        detail.mode,
                                        localeCode
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <MetricCard
                                    classes={classes}
                                    icon={<MdFavorite size={18} />}
                                    labelMsg="page.robots.cleaning_report.col_status"
                                    value={getCleaningTaskStatusLabel(
                                        detail.status,
                                        localeCode
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <MetricCard
                                    classes={classes}
                                    icon={<MdLayers size={18} />}
                                    labelMsg="page.robots.cleaning_report.detail.field.sub_mode"
                                    value={getCleaningSubModeLabel(
                                        detail.sub_mode,
                                        localeCode
                                    )}
                                />
                            </Grid>
                        </Grid>

                        <SectionCapsTitle
                            classes={classes}
                            icon={<MdMap size={16} />}
                            msg="page.robots.cleaning_report.detail.section.maps_tabs_caps"
                        />
                        {floors.length === 0 ? (
                            <Typography
                                color="textSecondary"
                                component="div"
                                className={classes.bodyMuted}
                            >
                                <LocaleMessage msg="page.robots.cleaning_report.detail.no_floors" />
                            </Typography>
                        ) : (
                            <>
                                {floors.length > 1 ? (
                                    <FormControl
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        style={{ marginBottom: 16 }}
                                    >
                                        <InputLabel
                                            id="detail-floor-select"
                                            shrink
                                        >
                                            {floorPickerLabel}
                                        </InputLabel>
                                        <Select
                                            labelId="detail-floor-select"
                                            label={floorPickerLabel}
                                            displayEmpty
                                            value={floorIndex}
                                            onChange={e =>
                                                setFloorIndex(
                                                    Number(e.target.value)
                                                )
                                            }
                                        >
                                            {floors.map((f, i) => (
                                                <MenuItem key={i} value={i}>
                                                    {dash(f.map_name) ||
                                                        `#${i + 1}`}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                ) : null}
                                <Tabs
                                    value={mapTab}
                                    onChange={(e, v) => setMapTab(v)}
                                    className={classes.mapTabsRoot}
                                    indicatorColor="primary"
                                    textColor="primary"
                                >
                                    <Tab
                                        className={classes.mapTab}
                                        label={
                                            <LocaleMessage msg="page.robots.cleaning_report.detail.tab.task_result_short" />
                                        }
                                    />
                                    <Tab
                                        className={classes.mapTab}
                                        label={
                                            <LocaleMessage msg="page.robots.cleaning_report.detail.tab.heatmap_short" />
                                        }
                                    />
                                </Tabs>
                                <div className={classes.mapPanel}>
                                    {mapTab === 0 && selectedFloor ? (
                                        <div
                                            className={classes.galleryNoSection}
                                        >
                                            <CleaningReportMapGallery
                                                floor={selectedFloor}
                                                slotKeys={[
                                                    CLEANING_REPORT_MAP_SLOT_KEYS.TASK_RESULT,
                                                ]}
                                                hideSectionLabel
                                                showEmptyPlaceholder
                                            />
                                        </div>
                                    ) : null}
                                    {mapTab === 1 && selectedFloor ? (
                                        <div
                                            className={classes.galleryNoSection}
                                        >
                                            <CleaningReportMapGallery
                                                floor={selectedFloor}
                                                slotKeys={[
                                                    CLEANING_REPORT_MAP_SLOT_KEYS.CLEAN_EFFECT,
                                                ]}
                                                hideSectionLabel
                                                showEmptyPlaceholder
                                            />
                                        </div>
                                    ) : null}
                                </div>
                            </>
                        )}

                        <SectionCapsTitle
                            classes={classes}
                            capsClassName={classes.sectionCapsZones}
                            icon={<MdLayers size={18} />}
                            msg="page.robots.cleaning_report.detail.section.zones_routes_caps"
                        />

                        {cleaningZoneRows.length === 0 &&
                        routeZoneRows.length === 0 ? (
                            <Typography
                                component="div"
                                className={classes.zonesEmptyHint}
                            >
                                <LocaleMessage msg="page.robots.cleaning_report.detail.no_floors" />
                            </Typography>
                        ) : (
                            <>
                                <Tabs
                                    value={zoneTab}
                                    onChange={(e, v) => setZoneTab(v)}
                                    className={classes.mapTabsRoot}
                                    indicatorColor="primary"
                                    textColor="primary"
                                >
                                    <Tab
                                        className={classes.mapTab}
                                        label={
                                            <LocaleMessage msg="pudu.area.tab.zones" />
                                        }
                                    />
                                    <Tab
                                        className={classes.mapTab}
                                        label={
                                            <LocaleMessage msg="pudu.area.tab.routes" />
                                        }
                                    />
                                </Tabs>

                                {(() => {
                                    const activeList =
                                        zoneTab === 0
                                            ? cleaningZoneRows
                                            : routeZoneRows;
                                    const isExpanded =
                                        zoneTab === 0
                                            ? expandedZones
                                            : expandedRoutes;
                                    const setExpanded =
                                        zoneTab === 0
                                            ? setExpandedZones
                                            : setExpandedRoutes;
                                    const visibleRows = activeList.slice(
                                        0,
                                        isExpanded ? activeList.length : 3
                                    );

                                    if (activeList.length === 0) {
                                        return (
                                            <Typography
                                                component="div"
                                                className={
                                                    classes.zonesEmptyHint
                                                }
                                            >
                                                <LocaleMessage msg="page.robots.cleaning_report.detail.no_floors" />
                                            </Typography>
                                        );
                                    }

                                    return (
                                        <div className={classes.zonesTableWrap}>
                                            <div className="table-responsive">
                                                <table className="table mb-0">
                                                    <thead>
                                                        <tr>
                                                            {floors.length >
                                                            1 ? (
                                                                <th>
                                                                    <LocaleMessage msg="page.robots.cleaning_report.detail.floor.col_map" />
                                                                </th>
                                                            ) : null}
                                                            <th>
                                                                <LocaleMessage msg="page.robots.cleaning_report.detail.floor.col_zone_name" />
                                                            </th>
                                                            <th>
                                                                <LocaleMessage msg="page.robots.cleaning_report.detail.floor.col_zone_type" />
                                                            </th>
                                                            <th>
                                                                <LocaleMessage msg="page.robots.cleaning_report.col_area" />
                                                            </th>
                                                            <th>
                                                                <LocaleMessage msg="page.robots.cleaning_report.detail.floor.col_clean_count" />
                                                            </th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {visibleRows.map(
                                                            (a, ai) => (
                                                                <tr
                                                                    key={`${a.area_id ||
                                                                        ai}-${a._mapName ||
                                                                        ''}`}
                                                                >
                                                                    {floors.length >
                                                                    1 ? (
                                                                        <td>
                                                                            {dash(
                                                                                a._mapName
                                                                            )}
                                                                        </td>
                                                                    ) : null}
                                                                    <td>
                                                                        {dash(
                                                                            a.area_name
                                                                        )}
                                                                    </td>
                                                                    <td>
                                                                        <Chip
                                                                            variant="outlined"
                                                                            className={
                                                                                classes.typeChip
                                                                            }
                                                                            label={getCleaningAreaTypeLabel(
                                                                                a.type,
                                                                                intl
                                                                            )}
                                                                        />
                                                                    </td>
                                                                    <td>
                                                                        {a.area ==
                                                                        null
                                                                            ? '—'
                                                                            : Number(
                                                                                  a.area
                                                                              ).toFixed(
                                                                                  2
                                                                              )}{' '}
                                                                        m²
                                                                    </td>
                                                                    <td>
                                                                        {dash(
                                                                            a.clean_count
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                            )
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                            {activeList.length > 3 && (
                                                <div
                                                    style={{
                                                        textAlign: 'center',
                                                        marginTop: 8,
                                                    }}
                                                >
                                                    <Button
                                                        size="small"
                                                        color="primary"
                                                        onClick={() =>
                                                            setExpanded(
                                                                !isExpanded
                                                            )
                                                        }
                                                    >
                                                        {isExpanded ? (
                                                            <LocaleMessage msg="page.robots.cleaning_report.detail.show_less" />
                                                        ) : (
                                                            <LocaleMessage msg="page.robots.cleaning_report.detail.show_more" />
                                                        )}
                                                    </Button>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })()}
                            </>
                        )}

                        {configEntries.length > 0 ? (
                            <>
                                <SectionCapsTitle
                                    classes={classes}
                                    capsClassName={classes.sectionCapsConfig}
                                    icon={<MdTune size={18} />}
                                    msg="page.robots.cleaning_report.detail.section.config_caps"
                                />
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        {configLeft.map(({ key, value }) => (
                                            <IntensityParam
                                                key={key}
                                                classes={classes}
                                                label={key}
                                                rawValue={value}
                                                localeCode={localeCode}
                                            />
                                        ))}
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        {configRight.map(({ key, value }) => (
                                            <IntensityParam
                                                key={key}
                                                label={key}
                                                rawValue={value}
                                                localeCode={localeCode}
                                                classes={classes}
                                            />
                                        ))}
                                    </Grid>
                                </Grid>
                            </>
                        ) : null}

                        <SectionCapsTitle
                            classes={classes}
                            icon={<MdMap size={16} />}
                            msg="page.robots.cleaning_report.detail.section.maps_metrics_caps"
                        />
                        <Grid container spacing={1}>
                            <Grid item xs={12} sm={4}>
                                <MetricCard
                                    classes={classes}
                                    icon={<MdPlace size={18} />}
                                    labelMsg="page.robots.cleaning_report.detail.metric.cleaned_area_short"
                                    value={
                                        detail.clean_area != null
                                            ? `${Number(
                                                  detail.clean_area
                                              ).toFixed(2)} m²`
                                            : '—'
                                    }
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <MetricCard
                                    classes={classes}
                                    icon={<MdTimer size={18} />}
                                    labelMsg="page.robots.cleaning_report.detail.metric.total_time"
                                    value={formatCleaningDuration(
                                        detail.clean_time
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <MetricCard
                                    classes={classes}
                                    icon={<MdFavorite size={18} />}
                                    labelMsg="page.robots.cleaning_report.col_status"
                                    value={getCleaningTaskStatusLabel(
                                        detail.status,
                                        localeCode
                                    )}
                                />
                            </Grid>
                        </Grid>
                    </>
                ) : null}
            </DialogContent>
            <DialogActions className={classes.dialogActions}>
                {detail && csvData.length > 0 ? (
                    <CSVLink
                        data={csvData}
                        headers={csvHeaders}
                        filename={GetFileName(
                            `REPORT-detail-${detail.report_id || 'task'}`,
                            'csv'
                        )}
                        onClick={() => {
                            toast.info(
                                <LocaleMessage msg="message.generating_file" />
                            );
                        }}
                        style={{ textDecoration: 'none' }}
                    >
                        <Button color="primary" size="small">
                            <LocaleMessage msg="button.export_data" />
                        </Button>
                    </CSVLink>
                ) : null}
                <Button onClick={onClose} color="primary" size="small">
                    <LocaleMessage msg="page.robots.cleaning_report.detail.close" />
                </Button>
            </DialogActions>
        </Dialog>
    );
}

CleaningReportDetailModal.propTypes = {
    intl: intlShape.isRequired,
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    loading: PropTypes.bool.isRequired,
    errorMessage: PropTypes.string,
    // eslint-disable-next-line react/forbid-prop-types -- payload Pudu (campos variam)
    detail: PropTypes.object,
    localeCode: PropTypes.string,
    chemicalDilutionRatio: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
    ]),
};

CleaningReportDetailModal.defaultProps = {
    errorMessage: null,
    detail: null,
    localeCode: 'pt_BR',
    chemicalDilutionRatio: 10,
};

export default injectIntl(CleaningReportDetailModal);
